import React, { useCallback, useRef, useState, type RefObject } from 'react';
import { Pressable, StyleSheet, View, type ScrollView, type TextInput } from 'react-native';
import { Text } from '@/components/common/text';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { Button } from '@/components/residents/marketplace/shared/Button';
import { UnifiedModal } from '@/components/common/UnifiedModal';
import { GlassCard } from '@/components/shared/redesign/GlassCard';
import {
  REDESIGN_SPACING,
  REDESIGN_TYPOGRAPHY,
} from '@/constants/residents/homeConstants';
import {
  SURVEY_LIMITS,
  SURVEY_QUESTION_TYPES,
} from '@/constants/residents/corkBoardConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { scrollIntoView } from '@/utils/common/scrollIntoView';
import type { UseSurveyDraftReturn } from '@/hooks/residents/corkBoard/useSurveyDraft';
import type { SurveyQuestionType } from '@/types/residents/corkBoard';
import { QuestionEditor } from './QuestionEditor';
import { SurveySettingsPanel } from './SurveySettingsPanel';

interface Props {
  draft: UseSurveyDraftReturn;
  showErrors?: boolean;
  /** The host's body ScrollView; when given, a newly added question is scrolled into view. */
  scrollRef?: RefObject<ScrollView | null>;
}

export const SurveyComposer: React.FC<Props> = ({ draft, showErrors = false, scrollRef }) => {
  const { colors } = useThemeColors();
  const [pickerOpen, setPickerOpen] = useState(false);
  const canAddMore = draft.questions.length < SURVEY_LIMITS.questionsMax;

  const rowRefs = useRef<(View | null)[]>([]);
  const promptRefs = useRef<(TextInput | null)[]>([]);
  // Index of the question just added from the picker, revealed once the picker has closed.
  const pendingRevealRef = useRef<number | null>(null);

  const handlePickType = useCallback(
    (type: SurveyQuestionType) => {
      pendingRevealRef.current = draft.questions.length;
      draft.addQuestion(type);
      setPickerOpen(false);
    },
    [draft],
  );

  // Runs after the picker sheet has unmounted. Doing this earlier is undone on
  // web: react-native-web restores focus to the "+ Add question" button when
  // the picker unmounts, and the browser scrolls that button into view — with
  // the new editor inserted above it, that clipped the editor's top. Natively
  // nothing scrolled or focused at all, so the user had to hunt for the card.
  const handlePickerClosed = useCallback(() => {
    const index = pendingRevealRef.current;
    pendingRevealRef.current = null;
    if (index == null) return;
    promptRefs.current[index]?.focus();
    const row = rowRefs.current[index];
    if (scrollRef && row) scrollIntoView(scrollRef, { current: row });
  }, [scrollRef]);

  return (
    <View style={styles.root}>
      <Text
        style={[
          REDESIGN_TYPOGRAPHY.sectionTitle,
          { color: colors.text.primary, marginBottom: REDESIGN_SPACING.sectionGap },
        ]}
      >
        Questions
      </Text>

      {draft.questions.length === 0 ? (
        <GlassCard
          variant="soft"
          dashed
          borderRadius={REDESIGN_SPACING.cardBorderRadius}
          padding={REDESIGN_SPACING.cardPadding}
        >
          <Text
            style={[
              REDESIGN_TYPOGRAPHY.body,
              { color: colors.text.secondary, textAlign: 'center' },
            ]}
          >
            No questions yet. Tap "Add question" to begin.
          </Text>
        </GlassCard>
      ) : (
        <View style={styles.questionList}>
          {draft.questions.map((item, i) => {
            const canMoveUp = i > 0;
            const canMoveDown = i < draft.questions.length - 1;
            return (
              <View
                key={`q-${i}`}
                ref={(node) => {
                  rowRefs.current[i] = node;
                }}
                testID={`survey-question-row-${i}`}
                style={styles.questionRow}
              >
                <View style={styles.reorderColumn}>
                  <Text style={[REDESIGN_TYPOGRAPHY.label, { color: colors.text.secondary }]}>
                    Q{i + 1}
                  </Text>
                  <Pressable
                    onPress={() => draft.reorderQuestion(i, i - 1)}
                    disabled={!canMoveUp}
                    hitSlop={6}
                    accessibilityLabel={`Move question ${i + 1} up`}
                    style={[styles.reorderBtn, !canMoveUp && styles.reorderBtnDisabled]}
                  >
                    <Ionicons
                      name="chevron-up"
                      size={20}
                      color={canMoveUp ? colors.text.secondary : colors.text.tertiary}
                    />
                  </Pressable>
                  <Pressable
                    onPress={() => draft.reorderQuestion(i, i + 1)}
                    disabled={!canMoveDown}
                    hitSlop={6}
                    accessibilityLabel={`Move question ${i + 1} down`}
                    style={[styles.reorderBtn, !canMoveDown && styles.reorderBtnDisabled]}
                  >
                    <Ionicons
                      name="chevron-down"
                      size={20}
                      color={canMoveDown ? colors.text.secondary : colors.text.tertiary}
                    />
                  </Pressable>
                </View>
                <View style={styles.questionBody}>
                  <QuestionEditor
                    question={item}
                    onChange={(patch) => draft.updateQuestion(i, patch)}
                    onDelete={() => draft.removeQuestion(i)}
                    promptRef={(node) => {
                      promptRefs.current[i] = node;
                    }}
                  />
                </View>
              </View>
            );
          })}
        </View>
      )}

      {canAddMore && (
        <Button
          title="+ Add question"
          variant="outline"
          size="medium"
          onPress={() => setPickerOpen(true)}
          style={styles.addBtn}
        />
      )}

      <Text
        style={[
          REDESIGN_TYPOGRAPHY.sectionTitle,
          {
            color: colors.text.primary,
            marginTop: REDESIGN_SPACING.contentGap,
            marginBottom: REDESIGN_SPACING.sectionGap,
          },
        ]}
      >
        Settings
      </Text>
      <SurveySettingsPanel
        isAnonymous={draft.isAnonymous}
        onIsAnonymousChange={draft.setIsAnonymous}
        allowReplies={draft.allowReplies}
        onAllowRepliesChange={draft.setAllowReplies}
        expiresAt={draft.expiresAt}
        onExpiresAtChange={draft.setExpiresAt}
      />

      {showErrors && draft.errors.length > 0 && (
        <GlassCard
          variant="soft"
          borderRadius={REDESIGN_SPACING.buttonRadius}
          padding={REDESIGN_SPACING.sectionGap}
          style={[
            styles.errorBox,
            { borderColor: colors.accent.red, backgroundColor: colors.accent.redBg },
          ]}
        >
          {draft.errors.map((e, i) => (
            <Text key={i} style={[REDESIGN_TYPOGRAPHY.small, { color: colors.accent.red }]}>
              {e}
            </Text>
          ))}
        </GlassCard>
      )}

      <UnifiedModal
        visible={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onClosed={handlePickerClosed}
        variant="sheet"
        title="Add a question"
        subtitle="Pick the question type"
        showHeader
      >
        <View style={styles.pickerList}>
          {SURVEY_QUESTION_TYPES.map((t) => (
            <Button
              key={t.value}
              title={t.label}
              variant="secondary"
              size="large"
              fullWidth
              onPress={() => handlePickType(t.value as SurveyQuestionType)}
            />
          ))}
        </View>
      </UnifiedModal>
    </View>
  );
};

const styles = StyleSheet.create({
  root: { gap: REDESIGN_SPACING.sectionGap },
  questionList: { gap: REDESIGN_SPACING.sectionGap },
  questionRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  reorderColumn: { paddingTop: 14, alignItems: 'center', gap: 4, width: 32 },
  reorderBtn: {
    paddingVertical: 2,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  reorderBtnDisabled: { opacity: 0.35 },
  questionBody: { flex: 1 },
  addBtn: { alignSelf: 'flex-start', marginTop: REDESIGN_SPACING.sectionGap },
  errorBox: { borderWidth: 1, marginTop: REDESIGN_SPACING.sectionGap, gap: 4 },
  pickerList: {
    gap: REDESIGN_SPACING.sectionGap,
    paddingVertical: REDESIGN_SPACING.sectionGap,
    paddingHorizontal: 24,
  },
});
